// src/services/taskGrouping.ts
// -----------------------------------------------------------------------------
// Pure ordering + grouping for the Responders Task List (ToDoPage).
//
// Two rules, decided 2026-06-30:
//   1. OLDEST FIRST — responders work the oldest outstanding items first.
//   2. STACK BY REQUEST — every task for the same request sits together, so a
//      request whose tasks were created days apart is no longer scattered down
//      the list. A group is positioned by its OLDEST task (so an old request
//      floats to the top and carries its newer tasks with it), and tasks WITHIN
//      a group are also oldest-first.
//
// Output is a flat, render-ready list (one entry per task) annotated with the
// group metadata the table needs to grey/blank repeat rows and show the
// request-level action buttons only on the first row of each group.
//
// Pure + framework-free so it can be unit-tested in isolation.
// -----------------------------------------------------------------------------

export interface OrderableTask {
  id?: number | string | null;
  request_id?: number | string | null;
  ref_no?: string | null;
  created_at?: string | null;
}

export interface GroupedTaskRow<T> {
  task: T;
  groupKey: string;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
  groupSize: number;
  // All tasks of this group, oldest-first. The same reference on every row of
  // the group; the table reads it on the first row to compute the group's
  // aggregate state (any unassigned? any assigned? all actioned?).
  groupTasks: T[];
}

// A request's tasks share a request_id; fall back to ref_no, then id, so a row
// with neither still renders as its own singleton group rather than collapsing
// everything keyless together.
export function groupKeyOf(t: OrderableTask): string {
  if (t.request_id != null && t.request_id !== "") return `r:${t.request_id}`;
  if (t.ref_no) return `ref:${t.ref_no}`;
  return `id:${t.id ?? ""}`;
}

// ISO-8601 timestamps compare correctly as plain strings; missing dates sort
// first (treated as oldest) so they never hide at the bottom.
function createdAt(t: OrderableTask): string {
  return t.created_at ?? "";
}

function cmp(a: string, b: string): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

export function groupAndOrderTasks<T extends OrderableTask>(tasks: T[]): GroupedTaskRow<T>[] {
  const groups = new Map<string, T[]>();
  for (const t of tasks) {
    const key = groupKeyOf(t);
    const existing = groups.get(key);
    if (existing) existing.push(t);
    else groups.set(key, [t]);
  }

  const ordered: { key: string; items: T[]; min: string }[] = [];
  for (const [key, items] of groups) {
    const sorted = items.slice().sort((a, b) => cmp(createdAt(a), createdAt(b)));
    ordered.push({ key, items: sorted, min: createdAt(sorted[0]) });
  }
  // Groups by oldest task; stable tie-break on the key for determinism.
  ordered.sort((a, b) => cmp(a.min, b.min) || cmp(a.key, b.key));

  const out: GroupedTaskRow<T>[] = [];
  for (const g of ordered) {
    const size = g.items.length;
    g.items.forEach((task, i) => {
      out.push({
        task,
        groupKey: g.key,
        isFirstInGroup: i === 0,
        isLastInGroup: i === size - 1,
        groupSize: size,
        groupTasks: g.items,
      });
    });
  }
  return out;
}
